/**
 * TanStack Query hooks for the dashboard's reads: the summary, the Standing score, windows, volume,
 * body map and reference.
 */

import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  SummaryKPIs,
  StandingResponse,
  VolumeResponse,
  WindowsResponse,
  BodyMapResponse,
  ReferenceResponse,
} from '../types'

/**
 * Fetch top-level dashboard KPIs.
 * Query key: ['summary']
 */
export function useSummary() {
  return useQuery({
    queryKey: ['summary'],
    queryFn: () => apiFetch<SummaryKPIs>('/api/summary'),
  })
}

/**
 * The composite Standing score + its three pillars and the causes behind the move.
 * Query key: ['dashboard', 'standing']
 */
export function useStanding() {
  return useQuery({
    queryKey: ['dashboard', 'standing'],
    queryFn: () => apiFetch<StandingResponse>('/api/dashboard/standing'),
  })
}

/**
 * Nightly sleep average for 7d / 30d / 90d / total. Training volume is
 * `useVolume()`.
 * Query key: ['dashboard', 'windows']
 */
export function useDashboardWindows() {
  return useQuery({
    queryKey: ['dashboard', 'windows'],
    queryFn: () => apiFetch<WindowsResponse>('/api/dashboard/windows'),
  })
}

/**
 * THE training volume: tonnage, working sets and training days for 7d / 30d /
 * 90d / total, plus the week-by-week series, from api/training_volume.py.
 * Every tonnage figure on the web reads this or a server field computed by
 * the same module — never a local reps × weight sum.
 * Query key: ['volume']
 */
export function useVolume() {
  return useQuery({
    queryKey: ['volume'],
    queryFn: () => apiFetch<VolumeResponse>('/api/volume'),
  })
}

/**
 * Per-region strength bands vs the age reference, for the body map.
 * Query key: ['dashboard', 'body-map']
 */
export function useBodyMap() {
  return useQuery({
    queryKey: ['dashboard', 'body-map'],
    queryFn: () => apiFetch<BodyMapResponse>('/api/dashboard/body-map'),
  })
}

/**
 * The 6 sarcopenia stations (best load vs age reference) behind the Strength
 * pillar. Query key: ['dashboard', 'reference']
 */
export function useReference() {
  return useQuery({
    queryKey: ['dashboard', 'reference'],
    queryFn: () => apiFetch<ReferenceResponse>('/api/dashboard/reference'),
  })
}
